import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import LocationOnRounded from '@mui/icons-material/LocationOnRounded';
import GitHubIcon from '@mui/icons-material/GitHub';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { Section, Eyebrow } from './Section';
import Reveal from './Reveal';
import { contactPath } from '../seo/routes';
import { azure, emerald, ink, line, slate } from '../theme';
import MsPartner from '../assets/img/mspartner.png';


export default function Contact() {
  const { t, i18n } = useTranslation();

  return (
    <Section id="contact" sx={{ pb: { xs: 8, md: 12 } }}>
      <Reveal>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 6,
            border: `1px solid ${alpha(azure[300], 0.28)}`,
            bgcolor: ink.raised,
            background: `
              radial-gradient(720px 380px at 88% -10%, ${alpha(azure[500], 0.2)}, transparent 60%),
              radial-gradient(560px 320px at 4% 110%, ${alpha(emerald[400], 0.12)}, transparent 60%),
              ${ink.raised}
            `,
            p: { xs: 3.5, sm: 6, md: 8 },
          }}
        >
          <Box sx={{ maxWidth: 640 }}>
            <Eyebrow>{t('contact.eyebrow')}</Eyebrow>
            <Typography variant="h2" component="h2">
              {t('contact.title')}
            </Typography>
            <Typography variant="subtitle1" sx={{ mt: 2.5, color: 'text.secondary' }}>
              {t('contact.lead')}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mt: 4, flexWrap: 'wrap', rowGap: 2 }}>
              <Button
                variant="contained"
                size="large"
                component={RouterLink}
                to={contactPath(i18n.language)}
                startIcon={<MailOutlineRounded />}
              >
                {t('contact.cta')}
              </Button>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, sm: 4 }}
              sx={{ mt: 5, pt: 3.5, borderTop: `1px solid ${line}` }}
            >
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <LocationOnRounded sx={{ color: azure[300], fontSize: 20, mt: '3px' }} />
                <Box>
                  <Typography variant="overline" component="p" sx={{ color: 'text.secondary' }}>
                    {t('contact.visit')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: slate[300] }}>
                    {t('contact.address')}
                  </Typography>
                </Box>
              </Stack>

              <Box>
                <Typography variant="overline" component="p" sx={{ color: 'text.secondary' }}>
                  {t('contact.follow')}
                </Typography>
                <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                  <IconButton
                    size="small"
                    href="https://github.com/dynapage"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="GitHub"
                  >
                    <GitHubIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
          </Box>

          {/* Microsoft Partner badge */}
          <Box
            sx={{
              position: { xs: 'static', md: 'absolute' },
              top: { md: 56 },
              insetInlineEnd: { md: 64 },
              mt: { xs: 4, md: 0 },
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'flex-start', md: 'flex-end' },
              gap: 1,
            }}
          >
            <Box component="img" src={MsPartner} alt="Microsoft Partner" sx={{ height: 52 }} />
            <Typography variant="caption" color="text.secondary">
              {t('contact.partner')}
            </Typography>
          </Box>
        </Box>
      </Reveal>
    </Section>
  );
}
